import '@/App.css';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import { ProtectedRoute } from './components/ProtectedRoute';
import Login from './pages/Login';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
// ── Self-Serve foundation demo (Module 1) ────────────────────────────
import TemplateDemoPage from './pages/_demo/TemplateDemoPage';
import StatsBoard from './pages/StatsBoard';
import BillJobs from './pages/BillJobs';
import BulkDownloads from './pages/BulkDownloads';
import Viewer from './pages/Viewer';
import ConsolidatedViewer from './pages/ConsolidatedViewer';
import Settings from './pages/Settings';
import SuperAdmin from './pages/SuperAdmin';
import AIManagerPage from './pages/AIManagerPage';
import Cockpit from './pages/Cockpit';
import UbmLocations from './pages/ubm/Locations';
import UbmSpaces from './pages/ubm/Spaces';
import UbmMeters from './pages/ubm/Meters';
import UbmVendorAccounts from './pages/ubm/VendorAccounts';
import EntitiesPage from './pages/ubm/EntitiesPage';
import PaymentVerification from './pages/PaymentVerificationV2';
import PaymentFileView from './pages/PaymentFileViewV2';
import PaymentConfig from './pages/PaymentConfig';
import PaymentsV2Page from './pages/payments/PaymentsV2Page';
import PaymentsBillsWorkspace from './pages/payments/PaymentsBillsWorkspace';
import ServiceFeeReport from './pages/payments/ServiceFeeReport';
import VarianceReport from './pages/payments/VarianceReport';
import RefundsReport from './pages/payments/RefundsReport';
import LateFeeReport from './pages/payments/LateFeeReport';
import EmergencyBillUpload from './pages/payments/EmergencyBillUpload';
import UploadCreditFilePage from './pages/payments/UploadCreditFilePage';
import CreditDebitReportPage from './pages/payments/CreditDebitReportPage';
import GlobalCreditDebitPage from './pages/payments/GlobalCreditDebitPage';
import Bill2RollinPage from './pages/ubm/Bill2RollinPage';
import PropertyDetailsPage from './pages/ubm/PropertyDetailsPage';
import PropertyEditPage from './pages/ubm/PropertyEditPage';
import Bill2RollinEditPage from './pages/ubm/Bill2RollinEditPage';
import DevToolsPage from './pages/superadmin/DevToolsPage';
import IntakeRollupPage from './pages/superadmin/IntakeRollupPage';
import ValidationReport from './pages/intake/ValidationReport';
import MissingBills from './pages/intake/MissingBills';
import PaymentsDashboard from './pages/payments/PaymentsDashboard';
import InvoicesPage from './pages/invoices/InvoicesPage';
import SyncJobDetail from './pages/ubm/SyncJobDetail';
import JobRunDetail from './pages/ubm/JobRunDetail';
import BillsPage from './pages/bills/BillsPage';
import BillViewPage from './pages/bills/BillViewPage';
import BillsDashboardPage from './pages/bills/BillsDashboardPage';
import OrgDashboardPage from './pages/dashboard/OrgDashboardPage';
import ContractsListPage from './pages/procurement/ContractsListPage';
import ContractDetailPage from './pages/procurement/ContractDetailPage';
import ContractsDashboardPage from './pages/procurement/ContractsDashboardPage';
import BudgetsListPage from './pages/procurement/BudgetsListPage';
import BudgetDetailPage from './pages/procurement/BudgetDetailPage';
import BudgetReportPage from './pages/procurement/BudgetReportPage';
import { isAuthenticated } from './lib/auth';
// AI Agent launcher now lives inside MainLayout's top nav (see AiAgentNavLauncher).

function App() {
  return (
    <>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={isAuthenticated() ? <Navigate to="/dashboard" /> : <Login />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <OrgDashboardPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/legacy"
            element={
              <ProtectedRoute>
                <StatsBoard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/bill-jobs"
            element={
              <ProtectedRoute>
                <BillJobs />
              </ProtectedRoute>
            }
          />
          <Route
            path="/intake-hub/validation-report"
            element={
              <ProtectedRoute>
                <ValidationReport />
              </ProtectedRoute>
            }
          />
          <Route
            path="/intake-hub/missing-bills"
            element={
              <ProtectedRoute>
                <MissingBills />
              </ProtectedRoute>
            }
          />
          <Route
            path="/super-admin/intake-rollup"
            element={
              <ProtectedRoute>
                <IntakeRollupPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/bulk-downloads"
            element={
              <ProtectedRoute>
                <BulkDownloads />
              </ProtectedRoute>
            }
          />
          <Route
            path="/viewer/:documentId"
            element={
              <ProtectedRoute>
                <Viewer />
              </ProtectedRoute>
            }
          />
          <Route
            path="/consolidated-viewer/:documentId"
            element={
              <ProtectedRoute>
                <ConsolidatedViewer />
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings"
            element={
              <ProtectedRoute>
                <Settings />
              </ProtectedRoute>
            }
          />
          <Route
            path="/super-admin"
            element={
              <ProtectedRoute>
                <SuperAdmin />
              </ProtectedRoute>
            }
          />
          <Route
            path="/super-admin/ai-manager"
            element={
              <ProtectedRoute>
                <AIManagerPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/cockpit"
            element={
              <ProtectedRoute>
                <Cockpit />
              </ProtectedRoute>
            }
          />
          {/* UBM Routes */}
          <Route
            path="/procurement/dashboard"
            element={
              <ProtectedRoute>
                <ContractsDashboardPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/procurement/contracts"
            element={
              <ProtectedRoute>
                <ContractsListPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/procurement/contracts/:contractId"
            element={
              <ProtectedRoute>
                <ContractDetailPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/procurement/budgets"
            element={
              <ProtectedRoute>
                <BudgetsListPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/procurement/budgets/:budgetId"
            element={
              <ProtectedRoute>
                <BudgetDetailPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/procurement/budgets/:budgetId/report"
            element={
              <ProtectedRoute>
                <BudgetReportPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/ubm/properties"
            element={
              <ProtectedRoute>
                <UbmLocations />
              </ProtectedRoute>
            }
          />
          <Route
            path="/ubm/units"
            element={
              <ProtectedRoute>
                <UbmSpaces />
              </ProtectedRoute>
            }
          />
          <Route
            path="/ubm/meters"
            element={
              <ProtectedRoute>
                <UbmMeters />
              </ProtectedRoute>
            }
          />
          <Route
            path="/ubm/vendors"
            element={
              <ProtectedRoute>
                <UbmVendorAccounts />
              </ProtectedRoute>
            }
          />
          <Route
            path="/ubm/entities"
            element={
              <ProtectedRoute>
                <EntitiesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/ubm/bill2rollin"
            element={
              <ProtectedRoute>
                <Bill2RollinPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/ubm/bill2rollin/property/:propertyId"
            element={
              <ProtectedRoute>
                <PropertyDetailsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/ubm/bill2rollin/edit/:accountIdRef"
            element={
              <ProtectedRoute>
                <Bill2RollinEditPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/ubm/bill2rollin/edit-property/:propertyId"
            element={
              <ProtectedRoute>
                <PropertyEditPage />
              </ProtectedRoute>
            }
          />
          {/* Payments V2 Routes */}
          <Route
            path="/payments/dashboard"
            element={
              <ProtectedRoute>
                <PaymentsDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/payments/bills"
            element={
              <ProtectedRoute>
                <PaymentsBillsWorkspace />
              </ProtectedRoute>
            }
          />
          <Route
            path="/payments/report"
            element={
              <ProtectedRoute>
                <PaymentsDashboard isGlobal={true} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/payments/workspace"
            element={
              <ProtectedRoute>
                <PaymentsBillsWorkspace isGlobal={true} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/payments"
            element={
              <ProtectedRoute>
                <PaymentsV2Page />
              </ProtectedRoute>
            }
          />
          <Route
            path="/payments/service-fees"
            element={
              <ProtectedRoute>
                <ServiceFeeReport />
              </ProtectedRoute>
            }
          />
          <Route
            path="/payments/variance"
            element={
              <ProtectedRoute>
                <VarianceReport />
              </ProtectedRoute>
            }
          />
          <Route
            path="/payments/refunds-report"
            element={
              <ProtectedRoute>
                <RefundsReport />
              </ProtectedRoute>
            }
          />
          <Route
            path="/payments/late-fee-report"
            element={
              <ProtectedRoute>
                <LateFeeReport />
              </ProtectedRoute>
            }
          />
          <Route
            path="/payments/emergency-bill-upload"
            element={
              <ProtectedRoute>
                <EmergencyBillUpload />
              </ProtectedRoute>
            }
          />
          <Route
            path="/payments/credit-debit"
            element={
              <ProtectedRoute>
                <CreditDebitReportPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/payments/upload-credit-file"
            element={
              <ProtectedRoute>
                <UploadCreditFilePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/super-admin/dev-tools"
            element={
              <ProtectedRoute>
                <DevToolsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/payments/global-credit-debit"
            element={
              <ProtectedRoute>
                <GlobalCreditDebitPage />
              </ProtectedRoute>
            }
          />
          {/* Invoices Route */}
          <Route
            path="/invoices"
            element={
              <ProtectedRoute>
                <InvoicesPage />
              </ProtectedRoute>
            }
          />
          {/* Bills Module Routes */}
          <Route
            path="/bills"
            element={
              <ProtectedRoute>
                <BillsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/bills/dashboard"
            element={
              <ProtectedRoute>
                <BillsDashboardPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/bills/:billId"
            element={
              <ProtectedRoute>
                <BillViewPage />
              </ProtectedRoute>
            }
          />
          {/* Sync Job Detail Route (opens in new tab) */}
          <Route
            path="/sync-job/:jobId"
            element={
              <ProtectedRoute>
                <SyncJobDetail />
              </ProtectedRoute>
            }
          />
          {/* Job Run Detail Route (opens in new tab) */}
          <Route
            path="/job-run/:runId"
            element={
              <ProtectedRoute>
                <JobRunDetail />
              </ProtectedRoute>
            }
          />
          {/* Payment Verification Routes (V1 - Legacy) */}
          <Route
            path="/payment-verification"
            element={
              <ProtectedRoute>
                <PaymentVerification />
              </ProtectedRoute>
            }
          />
          <Route
            path="/payment-verification/config"
            element={
              <ProtectedRoute>
                <PaymentConfig />
              </ProtectedRoute>
            }
          />
          <Route
            path="/payment-verification/:fileId"
            element={
              <ProtectedRoute>
                <PaymentFileView />
              </ProtectedRoute>
            }
          />
          <Route path="/" element={<Navigate to={isAuthenticated() ? '/dashboard' : '/login'} />} />
          {/* Self-Serve foundation demo (Module 1) */}
          <Route
            path="/admin/_template-demo"
            element={
              <ProtectedRoute>
                <TemplateDemoPage />
              </ProtectedRoute>
            }
          />
        </Routes>
        {/* AI Agent launcher now lives inside the top nav (see MainLayout.js → AiAgentNavLauncher) so it never overlaps drawers / modals. */}
      </BrowserRouter>
      <Toaster position="top-right" />
    </>
  );
}

export default App;
